import type { IChatInfo } from "@/chats/chats-models";
import { ALUA_SUPPORT_USERNAME } from "@/globals";
import { timeDiff } from "./time";
import { defined } from "./variable-evaluation";

export function createArray(length: number): null[] {
  if (length <= 0) {
    return [];
  }
  // Matches the historical behavior of `Array(...new Array(length))`: an
  // array of `length` holes, which both spread into and Array.from() as
  // `undefined` (not `null`) despite this function's declared return type.
  return Array.from({ length });
}

export function createArrayOf<C>(content: C, length: number): C[] {
  return Array.from({ length }, () => content);
}

export function setInArray<T>(arr: T[], index: number, val: T): T[] {
  return Object.assign([...arr], { [index]: val });
}

export function addToArrayIfNotThere<T>(arr: T[], val: T): T[] {
  if (arr.indexOf(val) > -1) {
    return arr;
  } else {
    return [...arr, val];
  }
}

export function addArrayToArrayIfNotThere<T>(arr1: T[], arr2: T[]): T[] {
  let result = [...arr1];
  arr2.forEach((val) => {
    if (arr1.indexOf(val) === -1) {
      result = [...result, val];
    }
  });
  return result;
}

export function removeInArray<T>(arr: T[], val: T): T[] {
  return arr.filter((arrItem) => arrItem !== val);
}

export function extractProp<O, K extends keyof O>(arr: O[], prop: K): O[K][] {
  return arr.map((item) => item[prop]);
}

export function removeInArrayBy<T, K extends keyof T>(
  prop: K,
  arr: T[],
  val: T[K]
): T[] {
  return arr.filter((item) => item[prop] !== val);
}

export function replaceInArrayBy<T, K extends keyof T>(
  prop: K,
  arr: T[],
  val: T[K],
  newItem: T
): T[] {
  return arr.map((item) => (item[prop] === val ? newItem : item));
}

export function sortByName(
  a: { name: string | number },
  b: { name: string | number }
) {
  if (typeof a.name === "number") {
    return a.name - Number(b.name);
  } else {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  }
}

export function sortBy<T extends Record<string, string | number>>(
  prop: keyof T,
  a: T,
  b: T
) {
  if (typeof a[prop] === "number") {
    return sortNumberBy(prop, a, b);
  } else {
    return sortCharBy(prop, a, b);
  }
}

function sortNumberBy<T extends Record<string, string | number>>(
  prop: keyof T,
  a: T,
  b: T
) {
  return Number(a[prop]) - Number(b[prop]);
}

function sortCharBy<T extends Record<string, string | number>>(
  prop: keyof T,
  a: T,
  b: T
) {
  if (a[prop] < b[prop]) {
    return -1;
  }
  if (a[prop] > b[prop]) {
    return 1;
  }
  return 0;
}

export function sortByCreatedAt(
  a: { created_at?: string },
  b: { created_at?: string }
) {
  if (timeDiff(a.created_at ?? "", b.created_at ?? "") < 0) {
    return -1;
  }
  if (timeDiff(a.created_at ?? "", b.created_at ?? "") > 0) {
    return 1;
  }
  return 0;
}

function isUnreadSupportChat(chat: IChatInfo): boolean {
  return (
    chat.unread_messages > 0 &&
    chat.other_user.username === ALUA_SUPPORT_USERNAME
  );
}

export function sortByLastMessageTime(chatA: IChatInfo, chatB: IChatInfo) {
  if (isUnreadSupportChat(chatB)) {
    return 1;
  }
  if (isUnreadSupportChat(chatA)) {
    return -1;
  }
  if (timeDiff(chatA.last_message_time, chatB.last_message_time) < 0) {
    return 1;
  }
  if (timeDiff(chatA.last_message_time, chatB.last_message_time) > 0) {
    return -1;
  }
  return 0;
}

export function sortNumber(a: number, b: number) {
  return a - b;
}

export function sortChar(a: string, b: string) {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
}

export function isXInArrayOf<T, K extends keyof T>(
  x: T[K],
  arr: T[],
  prop: K
): boolean {
  return arr.some((info) => info[prop] === x);
}

export function itemsInArrOf<T, K extends keyof T>(
  items: T[K][],
  arr: T[],
  prop: K
): T[] {
  return arr.filter((item) => items.indexOf(item[prop]) > -1);
}
export function arrOfProp<T, K extends keyof T>(arr: T[], prop: K): T[K][] {
  return arr.reduce((acc: T[K][], curr: T) => {
    if (acc.indexOf(curr[prop]) === -1) {
      acc.push(curr[prop]);
    }
    return acc;
  }, []);
}

export function flatten<T>(arr: Array<T | T[]>): T[] {
  return arr.reduce<T[]>((flat, toFlatten) => {
    return flat.concat(
      Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten
    );
  }, []);
}

export function findArrayIndex<T>(
  array: T[],
  predicateFunction: (item: T) => boolean
): number {
  const length = array == null ? 0 : array.length;
  if (!length) {
    return -1;
  }
  let index = -1;
  for (let i = 0; i < array.length; ++i) {
    if (predicateFunction(array[i])) {
      index = i;
      break;
    }
  }
  return index;
}

export function removeDuplicates<T, K extends keyof T>(
  arr: T[],
  idProp: K,
  removeFromStart?: boolean
): T[] {
  const mappedArr = arr.map((mapObj) => mapObj[idProp]);
  return arr.filter((obj, i) => {
    if (defined(obj[idProp])) {
      if (removeFromStart) {
        return mappedArr.lastIndexOf(obj[idProp]) === i;
      }
      return mappedArr.indexOf(obj[idProp]) === i;
    }
    return true;
  });
}

export function lastIndexInArrayWithValue<T, K extends keyof T>(
  arr: T[],
  prop: K,
  value: T[K]
): number | null {
  let index = arr.length - 1;
  for (; index >= 0; index--) {
    if (arr[index][prop] === value) {
      return index;
    }
  }
  return null;
}

export function minMax(items: number[]): number[] {
  return items.reduce<number[]>((range, value) => {
    if (range.length === 0) {
      return [value, value];
    }
    return [Math.min(range[0], value), Math.max(range[1], value)];
  }, []);
}
